/**
 * Tapetkalkylatorn på produktsidan (assets/wpcalc.bundle.js).
 *
 * Liten egen bundle: bara Vue plus kalkylatorn och rullmatematiken, ingen backend, ingen
 * inloggning. Vue-appen monteras först när kunden öppnar rutan, så sidladdningen inte betalar
 * för en modal de kanske aldrig öppnar.
 *
 * Liquid-kontrakt (snippets/bf2-wallpaper-calculator.liquid):
 *  - [data-bf2-wpcalc] bär produktens data och specs som JSON i data-specs / data-variant-specs.
 *  - [data-bf2-wpcalc-open] är öppna-knappen.
 *  - Antalet läggs över i produktsidans antalsfält, som pekas ut med data-qty-id.
 */
import { createApp, h, ref } from 'vue';
import WallpaperCalculator from '~/studio/WallpaperCalculator.vue';
import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf2-wpcalc', false);

function parseJson(raw, fallback) {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw);
    } catch (e) {
        debug.error('kunde inte tolka JSON', e);
        return fallback;
    }
}

function initRoot(root) {
    if (root.__bfWpcalc) return;
    root.__bfWpcalc = true;

    const openBtn = root.querySelector('[data-bf2-wpcalc-open]');
    const host = root.querySelector('[data-bf2-wpcalc-host]');
    if (!openBtn || !host) return;

    // Specs per variant, med produktens metafält som gemensam grund. Kalkylatorn följer den
    // variant kunden faktiskt tittar på (temat skickar variantChange på produktsektionen).
    const baseSpecs = parseJson(root.dataset.specs, null);
    const variantSpecs = parseJson(root.dataset.variantSpecs, {}) || {};
    const visualizeBgs = (parseJson(root.dataset.visualizeBgs, []) || []).filter(Boolean);
    const currentVariant = ref(root.dataset.variantId || '');

    const section = root.closest('[data-section-type="product"]');
    if (section) {
        section.addEventListener('variantChange', (event) => {
            const variant = event.detail && event.detail.variant;
            if (variant) currentVariant.value = String(variant.id);
        });
    }

    const open = ref(false);
    let app = null;

    function specsFor(id) {
        return variantSpecs[id] || baseSpecs;
    }

    // Djuplänken till rumsvisualiseraren bär vald colorway, så den öppnar på
    // exakt den variant kunden räknar på (inte produktens första variant).
    function visualizerUrlFor(id) {
        const base = root.dataset.visualizerUrl || '';
        if (!base || !id) return base;
        return base + (base.includes('?') ? '&' : '?') + 'variant=' + encodeURIComponent(id);
    }

    function mount() {
        if (app) return;
        app = createApp({
            setup() {
                return () =>
                    open.value
                        ? h(WallpaperCalculator, {
                              title: root.dataset.title || '',
                              handle: root.dataset.handle || '',
                              specs: specsFor(currentVariant.value),
                              visualizerUrl: visualizerUrlFor(currentVariant.value),
                              visualizeBgs: visualizeBgs,
                              disclaimer: root.dataset.disclaimer || '',
                              onClose: () => (open.value = false)
                          })
                        : null;
            }
        });
        app.mount(host);
    }

    openBtn.addEventListener('click', () => {
        mount();
        open.value = true;
        debug.log('kalkylator oppnad', root.dataset.handle);
    });
}

function init() {
    document.querySelectorAll('[data-bf2-wpcalc]').forEach(initRoot);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
